import {
  COURSES,
  DEFAULT_COURSE,
  PROTEIN_GROUPS,
  normaliseName,
  type Course,
  type FoodGroup,
} from '@shared/events'
import type { EffectiveDish, PlateBucket, RecipePayload } from '@shared/plate'
import type { Eleccion } from '../components/ElegirReceta'

/** Cómo se llama un plato en pantalla. */
export function nombreDePlato(
  dish: Pick<EffectiveDish, 'recipeId' | 'freeText'>,
  recipes: Map<string, RecipePayload>,
): string {
  if (dish.recipeId) return recipes.get(dish.recipeId)?.name ?? 'Receta borrada'
  return dish.freeText || 'Sin decidir'
}

/**
 * El rótulo del plato sólo estorba cuando la comida es un plato único: ahí
 * "Segundo" no dice nada. En cuanto hay dos, o el único no es el segundo, sí.
 */
export function mostrarCurso(dishes: readonly Pick<EffectiveDish, 'course'>[]): boolean {
  return dishes.length > 1 || dishes.some((dish) => dish.course !== DEFAULT_COURSE)
}

/** El sitio que toca al añadir otro plato a una franja: el primero libre. */
export function cursoLibre(ocupados: readonly Course[]): Course | null {
  if (!ocupados.includes(DEFAULT_COURSE)) return DEFAULT_COURSE
  return COURSES.find((course) => !ocupados.includes(course)) ?? null
}

/** Qué plato de la comida puede ser una receta; sin decirlo, segundo. */
export function cursosDeReceta(recipe: Pick<RecipePayload, 'courses'> | undefined): Course[] {
  const cursos = COURSES.filter((course) => recipe?.courses.includes(course))
  return cursos.length > 0 ? cursos : [DEFAULT_COURSE]
}

/**
 * Si un plato puesto es el que se está eligiendo: la misma receta, o el mismo
 * texto escrito a mano sin mirar mayúsculas ni tildes.
 */
export function esElMismoPlato(
  dish: Pick<EffectiveDish, 'recipeId' | 'freeText'>,
  eleccion: Eleccion,
): boolean {
  if (eleccion.recipeId) return dish.recipeId === eleccion.recipeId
  return !dish.recipeId && normaliseName(dish.freeText) === normaliseName(eleccion.freeText)
}

const BUCKET_DE_GRUPO: Partial<Record<FoodGroup, PlateBucket>> = {
  verdura: 'vegetal',
  fruta: 'vegetal',
  cereal: 'cereal',
  ...Object.fromEntries(PROTEIN_GROUPS.map((group) => [group, 'proteina'])),
}

/** Qué porciones del plato aporta una receta, para pintar sus puntos. */
export function porcionesDeReceta(recipe: RecipePayload): Set<PlateBucket> {
  const buckets = new Set<PlateBucket>()
  for (const ingredient of recipe.ingredients) {
    const bucket = ingredient.group ? BUCKET_DE_GRUPO[ingredient.group] : undefined
    if (bucket) buckets.add(bucket)
  }
  return buckets
}

export const COLOR_PORCION: Record<PlateBucket, string> = {
  vegetal: 'bg-sage',
  proteina: 'bg-primary',
  cereal: 'bg-apricot',
}
